import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button, Input, Modal, Text } from '@fragments';
import { IconAlertCircle, IconCheck, IconChevronDown, IconChevronLeft, IconCloudCheck, IconDeviceMobile, IconLoader2, IconMessageBolt, IconRefresh } from '@tabler/icons-react';
import { LIMITS } from '../../../../enums/automations';
import { STUDIO_AUTOMATIONS_LIST, automationPath } from '../../../../enums/routes';
import PageState from '../../components/PageState';
import PhonePreview from '../../components/PhonePreview';
import StatusMark from '../../components/StatusMark';
import TriggerStep from './components/TriggerStep';
import KeywordStep from './components/KeywordStep';
import MessagesStep from './components/MessagesStep';
import DeliveryStep from './components/DeliveryStep';
import useAutomationEditor from './hooks/useAutomationEditor';
import './styles.scss';

const BODIES = { trigger: TriggerStep, keywords: KeywordStep, messages: MessagesStep, delivery: DeliveryStep };
const TRANSITION = { duration: 0.18, ease: 'easeOut' };

// Which scene of the phone a step shows.
const sceneOf = (step, channel) => {
  if (channel === 'direct_messages' || step === 'messages' || step === 'delivery') return 'chat';
  if (channel === 'stories') return 'story';
  return step === 'trigger' ? 'post' : 'comments';
};

// A step's closed line: what it says now.
function useSummaries(s, editor) {
  const { t } = useTranslation('studioAutomations');
  const { trigger, comment_reply: reply, dm } = editor.draft;
  const media = trigger.media_scope === 'all' ? t(trigger.channel === 'stories' ? 'scopeAllStories' : 'scopeAll') : trigger.media_scope === 'next' ? t(trigger.channel === 'stories' ? 'scopeNextStory' : 'scopeNext') : t('summaryMedia', { count: trigger.media_ids.length });
  const sequence = [dm.opening.enabled && t('openingTitle'), dm.follow_gate.enabled && t('followShort'), dm.email_capture.enabled && t('emailShort')].filter(Boolean);
  const links = dm.delivery.buttons.filter(button => button.title && button.url).length;
  return {
    trigger: trigger.channel === 'direct_messages' ? t('channel_direct_messages') : `${t(`channel_${trigger.channel}`)} · ${media}`,
    keywords: [trigger.match === 'any' ? t('matchAny') : trigger.keywords.join(', ') || t('summaryNoKeywords'), reply.enabled && t('summaryReply', { count: reply.templates.filter(item => item.trim()).length })].filter(Boolean).join(' · '),
    messages: sequence.length ? sequence.join(' · ') : t('summaryNoSequence'),
    delivery: [t('summaryLinks', { count: links }), dm.reminder.enabled && t('reminderShort')].filter(Boolean).join(' · '),
  };
}

function SaveState({ editor }) {
  const { t } = useTranslation('studioAutomations');
  const { saveState, isActive } = editor;
  if (saveState === 'failed') return <span className="studio-automation-editor__save is-failed"><Text.caption contrast={8} left={<IconAlertCircle />}>{t('saveFailed')}</Text.caption><Button size="micro" variant="text" color="overlay" preventLoading onClick={editor.retrySave}>{t('studio:retry')}</Button></span>;
  if (saveState === 'saving') return <Text.caption contrast={6} left={<IconLoader2 className="studio-automation-spin" />}>{t('saving')}</Text.caption>;
  if (saveState === 'dirty') return <Text.caption contrast={6}>{t(isActive ? 'unsaved' : 'saving')}</Text.caption>;
  return <Text.caption contrast={6} left={<IconCloudCheck />}>{t('saved')}</Text.caption>;
}

// /studio/automations/<id>/edit — the automation read as one sentence: when someone does this, and
// says that, they receive this, and then that. One part open at a time, with the phone beside it
// showing what the person goes through.
export default function Editor({ studio: s, id }) {
  const { t } = useTranslation('studioAutomations');
  const editor = useAutomationEditor(s, id);
  const [previewing, setPreviewing] = useState(false);
  const reduced = useReducedMotion();
  if (editor.status === 'loading') return <PageState><Text.h4 loading>{t('title')}</Text.h4></PageState>;
  if (editor.status === 'missing') return <PageState icon={<IconMessageBolt />} text={t('automationMissing')} action={<Button variant="rounded" color="inverse" preventLoading onClick={() => s.navigate(STUDIO_AUTOMATIONS_LIST)}>{t('backToList')}</Button>} />;
  if (editor.status === 'error') return <PageState icon={<IconMessageBolt />} text={t('loadError')} action={<Button variant="rounded" color="overlay" startIcon={<IconRefresh />} preventLoading onClick={editor.reload}>{t('studio:retry')}</Button>} />;
  return <EditorView studio={s} editor={editor} previewing={previewing} setPreviewing={setPreviewing} reduced={reduced} id={id} />;
}

function EditorView({ studio: s, editor, previewing, setPreviewing, reduced, id }) {
  const { t } = useTranslation('studioAutomations');
  const summaries = useSummaries(s, editor);
  const { draft, automation, step, isActive } = editor;
  // The part just opened comes into view once the previous one has folded.
  const parts = useRef({});
  const opened = useRef(step);
  useEffect(() => {
    if (opened.current === step) return undefined;
    opened.current = step;
    const timer = setTimeout(() => parts.current[step]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' }), TRANSITION.duration * 1000 + 20);
    return () => clearTimeout(timer);
  }, [step, reduced]);
  const channel = draft.trigger.channel;
  const account = { handle: s.profile.handle, picture: s.profile.picture };
  const phone = <PhonePreview automation={draft} scene={sceneOf(step, channel)} until={step === 'messages' ? 'messages' : undefined} account={account} media={editor.previewMedia} locale={s.locale} />;
  const motionProps = reduced ? {} : { initial: { height: 0, opacity: 0 }, animate: { height: 'auto', opacity: 1 }, exit: { height: 0, opacity: 0 }, transition: TRANSITION };
  const lastStep = editor.steps[editor.steps.length - 1].key === step;
  const go = isActive ? editor.saveActive : editor.activate;
  const activateLabel = isActive ? t('saveActive') : t(automation.status === 'paused' ? 'resume' : 'activate');
  return <div className="studio-automation-editor">
    <div className="studio-automation-editor__form">
      <header className="studio-automation-editor__head">
        <Button variant="rounded" discreet color="overlay" icon={<IconChevronLeft />} aria-label={t('backToList')} tooltip={t('backToList')} preventLoading onClick={() => s.navigate(automation.status === 'draft' ? STUDIO_AUTOMATIONS_LIST : automationPath(id))} />
        <div className="studio-automation-editor__name">
          <Input value={draft.name} onInput={value => editor.update('name', value.slice(0, LIMITS.name))} inputSize="small" fullWidth aria-label={t('nameLabel')} placeholder={t('namePlaceholder')} />
        </div>
        <StatusMark status={automation.status} />
      </header>
      <div className="studio-automation-editor__actions">
        <SaveState editor={editor} />
        <span className="studio-automation-editor__preview-toggle"><Button variant="rounded" color="overlay" size="small" startIcon={<IconDeviceMobile />} preventLoading onClick={() => setPreviewing(true)}>{t('previewOpen')}</Button></span>
        <Button variant="rounded" color="inverse" size="small" loading={editor.busy === 'activate' || editor.busy === 'save'} disabled={isActive && editor.saveState === 'saved'} preventLoading onClick={go}>{activateLabel}</Button>
      </div>
      <ol className="studio-automation-editor__steps">
        {editor.steps.map(({ key, errors }, index) => {
          const open = step === key;
          const Body = BODIES[key];
          return <li key={key} ref={node => { parts.current[key] = node; }} className={`studio-automation-step${open ? ' is-open' : ''}${errors ? ' has-errors' : ''}`}>
            <button type="button" className="studio-automation-step__head" aria-expanded={open} onClick={() => editor.setStep(key)}>
              <span className="studio-automation-step__mark" aria-hidden="true">{errors ? '!' : !open && index < editor.steps.findIndex(item => item.key === step) ? <IconCheck /> : index + 1}</span>
              <span className="studio-automation-step__heading">
                <Text.label contrast={open ? 9 : 8}>{t(`stepTitle_${key}_${channel}`)}</Text.label>
                {!open && <Text.caption contrast={6} maxLines={1}>{summaries[key]}</Text.caption>}
              </span>
              <IconChevronDown className="studio-automation-step__chevron" />
            </button>
            <AnimatePresence initial={false}>
              {open && <motion.div key="body" className="studio-automation-step__motion" {...motionProps}>
                <div className="studio-automation-step__body">
                  <Body studio={s} editor={editor} />
                  <div className="studio-automation-step__next">
                    {lastStep ? <Button variant="rounded" color="inverse" loading={editor.busy === 'activate' || editor.busy === 'save'} disabled={isActive && editor.saveState === 'saved'} preventLoading onClick={go}>{activateLabel}</Button>
                      : <Button variant="rounded" color="overlay" preventLoading onClick={editor.goNext}>{t('next')}</Button>}
                  </div>
                </div>
              </motion.div>}
            </AnimatePresence>
          </li>;
        })}
      </ol>
    </div>
    <aside className="studio-automation-editor__preview" aria-label={t('previewLabel')}>
      <Text.caption contrast={6}>{t('previewLabel')}</Text.caption>
      <div className="studio-automation-editor__phone">{phone}</div>
    </aside>

    {previewing && <Modal open size={360} variant="flat" closeLabel={t('studio:close')} onClose={() => setPreviewing(false)} className="studio-automation-editor__preview-modal">{phone}</Modal>}
    {editor.gate === 'limit' && <Modal open size="sm" title={t('gateLimitTitle')} subtitle={t('gateLimitText', { limit: s.automations.overview?.quota.limit ?? 0 })} closeLabel={t('studio:close')} onClose={() => editor.setGate(null)}
      footer={<>
        <Button variant="rounded" color="overlay" preventLoading onClick={() => { editor.setGate(null); s.navigate(STUDIO_AUTOMATIONS_LIST); }}>{t('gateLimitList')}</Button>
        {s.payments.nextPlan && <Button variant="rounded" color="ai" preventLoading onClick={() => { editor.setGate(null); s.automations.upgrade(s.payments.nextPlan); }}>{t('gateUpgrade', { plan: t(`studio:planName_${s.payments.nextPlan}`) })}</Button>}
      </>} />}
    {editor.gate === 'connection' && <Modal open size="sm" title={t('gateConnectionTitle')} subtitle={t('gateConnectionText')} closeLabel={t('studio:close')} onClose={() => editor.setGate(null)}
      footer={<Button variant="rounded" color="inverse" preventLoading onClick={editor.openConnect}>{t('heroAction')}</Button>} />}
    {editor.gate === 'conflict' && <Modal open size="sm" title={t('gateConflictTitle')} subtitle={t('gateConflictText')} closeLabel={t('studio:close')} onClose={() => editor.setGate(null)}
      footer={<Button variant="rounded" color="inverse" preventLoading onClick={() => { editor.setGate(null); editor.reload(); }}>{t('gateConflictReload')}</Button>} />}
  </div>;
}
